import React, { createContext, useContext, useReducer, ReactNode, useEffect, useCallback, useRef } from 'react';
import { websocketService } from '../services/websocketService';
import { markBecameHost, rememberReferral } from '../services/growthService';
import { useAuth } from './AuthContext';
import { HouseRulesSettings, defaultHouseRules } from '../components/HouseRules';

// Types
export interface Player {
  id: string;
  name: string;
  avatar: string;
  isReady: boolean;
  isHost: boolean;
  isBot?: boolean;
  score: number;
  currentAnswers?: Record<string, string>;
  isSubmitted?: boolean;
  /** Server-broadcast presence flag. Bots are always true. Defaults to true
   *  on the client when missing so we never grey-out players just because
   *  an old server emitted a payload without it. */
  isConnected?: boolean;
  /**
   * Has this player already handed in this round's sheet?
   *
   * The server has always sent this on every roster broadcast (`serializePlayer`);
   * the type simply never declared it, so nothing could read it. It is the ONLY
   * way a player resuming an async game can know who has already gone — the
   * `answer_submitted` events that build that picture in a live game were
   * broadcast hours ago, to a room they were not in.
   */
  hasSubmitted?: boolean;
}

export interface GameSettings {
  rounds: number;
  timeLimit: number;
  categories: string[];
  mode: 'standard' | 'fast' | 'ultra' | 'daily' | 'custom' | 'async';
  houseRules: HouseRulesSettings;
  /**
   * WHO CAN JOIN. `true` = invite-only (the default, and the family path);
   * `false` = listed in Public Games, where strangers can see it and walk in.
   *
   * The server is the only source of truth for this — it arrives on
   * `room_created` and on every `gameSettingsUpdated`. The lobby toggle reads
   * it from here rather than keeping its own copy, so a non-host sees the room
   * being opened to strangers the instant the host does it.
   */
  isPrivate?: boolean;
  /**
   * THE LADDER. A run: Atom escalates every round and you have one life.
   *
   * `rounds` is 26 for every run (the alphabet — nobody will ever reach it), so
   * anything that renders a round counter has to check this and drop the
   * denominator. "Round 3 / 26" tells a player they have twenty-three rounds
   * left; they have one life.
   */
  isLadder?: boolean;
  /** Async / correspondence mode — no per-round timer; round ends when
   *  every player has submitted (or host force-ends). */
  isAsync?: boolean;
  /** Per-turn time budget in async mode, before host can force-advance. */
  turnTimeoutHours?: number;

  /**
   * WHICH GAME ARE WE PLAYING? Null is Classic.
   *
   * `categories` above is now DERIVED from this — the server refuses to take a
   * category off the wire, because that is how `Guitar` used to score 10 as a
   * Food. A category is a pack's column or it does not exist.
   *
   * NEVER THE KEY. Name, emoji, kind, column names and letter counts only: a
   * hallucinated key entry is inert exactly as long as no player can read the key.
   */
  packId?: string | null;
  pack?: PackSummary | null;
}

/** What a client is allowed to know about a pack. Deliberately not much. */
export interface PackSummary {
  id: string;
  name: string;
  emoji: string;
  blurb: string;
  kind: 'CORPUS' | 'KEYED' | 'OPEN' | 'TABLE';
  columns: Array<{ name: string; emoji: string }>;
  builtin: boolean;
  shelf: boolean;
  visibility: string;
  ownerUid: string | null;
  /** Words this table has taught it. The number that makes A–Z of Us real. */
  learnedCount: number;
  timeLimit: number;
}

export interface GameRound {
  roundNumber: number;
  letter: string;
  timeRemaining: number;
  isActive: boolean;
  submissions: Record<string, Record<string, string>>;
  scores: Record<string, number>;
}

export interface GameState {
  // Player data
  currentPlayer: Player | null;
  
  // Room state
  roomCode: string | null;
  isHost: boolean;
  players: Player[];
  
  // Game configuration
  gameSettings: GameSettings;
  
  // Game session
  isGameActive: boolean;
  currentRound: number;
  totalRounds: number;
  rounds: GameRound[];
  gameStartTime: number | null;
  
  // Current round state
  currentLetter: string;
  timeRemaining: number;
  isSubmitted: boolean;
  currentAnswers: Record<string, string>;
  
  // Results
  finalResults: any | null;
  
  // UI state
  currentScreen: 'splash' | 'setup' | 'home' | 'lobby' | 'game' | 'results';
  isLoading: boolean;
  error: string | null;
  
  // WebSocket state
  isConnected: boolean;
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
}

// Actions
type GameAction =
  | { type: 'SET_PLAYER'; payload: Player }
  | { type: 'CLEAR_PLAYER' }
  | { type: 'JOIN_ROOM'; payload: { roomCode: string; players: Player[] } }
  | { type: 'LEAVE_ROOM' }
  | { type: 'UPDATE_PLAYERS'; payload: Player[] }
  | { type: 'UPDATE_TOTAL_SCORES'; payload: Record<string, number> }
  | { type: 'UPDATE_GAME_SETTINGS'; payload: Partial<GameSettings> }
  | { type: 'SET_ROOM_CODE'; payload: string }
  | { type: 'SET_HOST'; payload: boolean }
  | { type: 'START_GAME'; payload: GameSettings }
  | { type: 'START_ROUND'; payload: { roundNumber: number; letter: string } }
  | { type: 'UPDATE_TIMER'; payload: number }
  | { type: 'UPDATE_ANSWERS'; payload: Record<string, string> }
  | { type: 'SUBMIT_ANSWERS' }
  | { type: 'ROUND_COMPLETE'; payload: { scores: Record<string, number> } }
  | { type: 'GAME_COMPLETE'; payload: any }
  | { type: 'SET_GAME_RESULTS'; payload: any }
  | { type: 'SET_SCREEN'; payload: GameState['currentScreen'] }
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CONNECTION_STATUS'; payload: GameState['connectionStatus'] }
  | { type: 'RESTORE_GAME_STATE'; payload: any };

// Initial state
// Exported alongside gameReducer, for the same tests.
export const initialState: GameState = {
  currentPlayer: null,
  roomCode: null,
  isHost: false,
  players: [],
  gameSettings: {
    rounds: 5,
    timeLimit: 60,
    categories: ['Name', 'Place', 'Animal', 'Thing'],
    mode: 'standard',
    houseRules: defaultHouseRules
  },
  isGameActive: false,
  currentRound: 0,
  totalRounds: 5,
  rounds: [],
  gameStartTime: null,
  currentLetter: '',
  timeRemaining: 0,
  isSubmitted: false,
  currentAnswers: {},
  finalResults: null,
  currentScreen: 'splash',
  isLoading: false,
  error: null,
  isConnected: false,
  connectionStatus: 'disconnected'
};

// Reducer
// Exported for the round-boundary tests: the mid-round-lockout bug (see
// START_ROUND) lived entirely inside this function's state transitions, and a
// transition you cannot test outside a browser is how it shipped.
export function gameReducer(state: GameState, action: GameAction): GameState {
  switch (action.type) {
    case 'SET_PLAYER':
      return {
        ...state,
        currentPlayer: action.payload
      };

    case 'CLEAR_PLAYER':
      return {
        ...state,
        currentPlayer: null
      };

    case 'JOIN_ROOM':
      return {
        ...state,
        roomCode: action.payload.roomCode,
        players: action.payload.players,
        isHost: action.payload.players.some(p => p.id === state.currentPlayer?.id && p.isHost) || false
      };

    case 'LEAVE_ROOM':
      return {
        ...state,
        roomCode: null,
        players: [],
        isHost: false,
        isGameActive: false,
        currentScreen: 'home'
      };

    case 'UPDATE_PLAYERS':
      return {
        ...state,
        players: action.payload
      };

    case 'UPDATE_TOTAL_SCORES': {
      // Server sends a {playerId: total} map after every round. Merge it
      // into the player list rather than reading state.players in the
      // websocket handler closure (which is stale).
      const totals = action.payload;
      return {
        ...state,
        players: state.players.map(p => ({
          ...p,
          score: totals[p.id] !== undefined ? totals[p.id] : (p.score ?? 0),
        })),
      };
    }

    case 'UPDATE_GAME_SETTINGS':
      return {
        ...state,
        gameSettings: { ...state.gameSettings, ...action.payload }
      };

    case 'SET_ROOM_CODE':
      return {
        ...state,
        roomCode: action.payload
      };

    case 'SET_HOST':
      return {
        ...state,
        isHost: action.payload
      };

    case 'START_GAME':
      return {
        ...state,
        isGameActive: true,
        gameSettings: action.payload,
        totalRounds: action.payload.rounds,
        currentRound: 0,
        rounds: [],
        currentScreen: 'game',
        gameStartTime: Date.now(),
        // Preserve players array
        players: state.players
      };

    case 'START_ROUND':
      const newRound: GameRound = {
        roundNumber: action.payload.roundNumber,
        letter: action.payload.letter,
        timeRemaining: state.gameSettings.timeLimit,
        isActive: true,
        submissions: {},
        scores: {}
      };
      
      return {
        ...state,
        currentRound: action.payload.roundNumber,
        currentLetter: action.payload.letter,
        timeRemaining: state.gameSettings.timeLimit,
        isSubmitted: false,
        currentAnswers: {},
        rounds: [...state.rounds, newRound],
        /*
          MIRROR THE SERVER'S PER-ROUND RESET, OR THE FIELDS DIE MID-ROUND.

          The server zeroes every player's `hasSubmitted` the instant a round
          starts — but `roundStarted` carries no roster, so nothing ever
          zeroed OUR copy. Any snapshot taken while people had submitted (a
          `players_updated` fired by a phone locking mid-tally, a
          `gameStateRestore` after a screen-lock reconnect — routine in a
          seven-player living room) left `hasSubmitted: true` sitting in
          state.players across the round boundary. The first event that
          re-mapped the array in the NEW round copied those stale flags into a
          fresh reference, GameScreen's roster-sync effect read them as "these
          players have submitted THIS round", and a player who hadn't typed a
          word was locked out — grey fields, keyboard closed, clock running.
          One player in a real 7-player game blanked 20 cells out of 20 this
          way. Clearing the flags here is the same one line the server runs,
          at the same moment.
        */
        players: state.players.map(p =>
          p.hasSubmitted || p.isSubmitted
            ? { ...p, hasSubmitted: false, isSubmitted: false }
            : p
        )
      };

    case 'UPDATE_TIMER':
      return {
        ...state,
        timeRemaining: action.payload
      };

    case 'UPDATE_ANSWERS':
      return {
        ...state,
        currentAnswers: action.payload
      };

    case 'SUBMIT_ANSWERS':
      return {
        ...state,
        isSubmitted: true,
        // Preserve players array
        players: state.players
      };

    case 'ROUND_COMPLETE':
      const updatedRounds = state.rounds.map(round => 
        round.roundNumber === state.currentRound 
          ? { ...round, isActive: false, scores: action.payload.scores }
          : round
      );
      
      return {
        ...state,
        rounds: updatedRounds,
        // Preserve players array
        players: state.players
      };

    case 'GAME_COMPLETE':
      return {
        ...state,
        isGameActive: false,
        finalResults: action.payload,
        currentScreen: 'results',
        // Preserve players array
        players: state.players
      };

    case 'SET_GAME_RESULTS':
      return {
        ...state,
        finalResults: action.payload
      };

    case 'SET_SCREEN':
      return {
        ...state,
        currentScreen: action.payload
      };

    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload
      };

    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload
      };

    case 'SET_CONNECTION_STATUS':
      return {
        ...state,
        connectionStatus: action.payload,
        isConnected: action.payload === 'connected'
      };

    case 'RESTORE_GAME_STATE': {
      // Server-authoritative rehydration after refresh/reconnect. Pulls
      // round / letter / players / timer / settings into local state and
      // jumps the UI to the active screen (lobby vs game vs results).
      // Falls back to localStorage backup if server says active=false but
      // we have recent local state — covers the gap where the user is
      // mid-game but Cloud Run cold-started during the refresh.
      const p = action.payload || {};
      const useServer = p.active && p.roomCode;
      let source = useServer ? p : null;
      if (!source) {
        try {
          const cached = JSON.parse(localStorage.getItem('wr.gameState') || 'null');
          if (cached && Date.now() - (cached.savedAt || 0) < 5 * 60 * 1000) {
            source = cached;
          }
        } catch {}
      }
      if (!source) return state;

      const isPlaying = source.status === 'playing';
      const isFinished = source.status === 'completed';
      return {
        ...state,
        roomCode: source.roomCode,
        isHost: !!source.isHost,
        players: Array.isArray(source.players) ? source.players : state.players,
        gameSettings: { ...state.gameSettings, ...(source.gameSettings || {}) },
        isGameActive: isPlaying,
        currentRound: source.currentRound || state.currentRound,
        totalRounds: source.totalRounds || state.totalRounds,
        currentLetter: source.currentLetter || state.currentLetter || '',
        timeRemaining: source.timeRemaining || 0,
        currentScreen: isFinished ? 'results' : isPlaying ? 'game' : 'lobby',
      };
    }

    default:
      return state;
  }
}

// Context
const GameContext = createContext<{
  state: GameState;
  dispatch: React.Dispatch<GameAction>;
} | null>(null);

// Provider
export function GameProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(gameReducer, initialState);

  return (
    <GameContext.Provider value={{ state, dispatch }}>
      {children}
    </GameContext.Provider>
  );
}

// Hook
export function useGame() {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}

// Add WebSocket Context
interface WebSocketContextType {
  isConnected: boolean;
  connectionStatus: 'disconnected' | 'connecting' | 'connected' | 'error';
  currentRoom: string | null;
  connect: () => Promise<void>;
  disconnect: () => void;
  authenticate: (name: string, avatar: string) => Promise<any>;
  createRoom: (options?: any) => Promise<string>;
  joinRoom: (roomCode: string) => Promise<void>;
  leaveRoom: () => Promise<void>;
  setPlayerReady: (isReady: boolean) => Promise<void>;
  startGame: () => Promise<void>;
  /**
   * Solo: create the room, make sure exactly one Atom is in it, and START —
   * all in one shot. The user never sees a lobby.
   */
  createSoloGame: (options: { rounds: number; difficulty: string }) => Promise<string>;
  createLadderRun: () => Promise<string>;
  reinitializeConnection: () => Promise<void>;
  sendChatMessage: (message: string) => Promise<void>;
  submitAnswers: (answers: Record<string, string>) => Promise<void>;
  kickPlayer: (playerId: string) => Promise<void>;
  transferHost: (playerId: string) => Promise<void>;
  addBot: (difficulty?: string) => Promise<void>;
  getCurrentRoom: () => string | null;
  requestRematch: () => Promise<void>;
}

const WebSocketContext = createContext<WebSocketContextType | null>(null);

export function useWebSocket() {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
}

// WebSocket Provider Component
export function WebSocketProvider({ children }: { children: ReactNode }) {
  const { state, dispatch } = useGame();
  const { currentUser, userProfile } = useAuth();
  const initialized = useRef(false);
  // Auto-rejoin runs once per session — without this guard, every userProfile
  // refresh re-triggered the rejoin and hijacked fresh "create room" flows
  // (Solo Setup created a room; the rejoin then forced us into a stale one).
  const rejoinAttempted = useRef(false);
  // Mirror state into a ref so socket-event handlers (which capture the
  // closure once on mount) can always read the latest values without
  // re-attaching listeners on every state change.
  const latestStateRef = useRef(state);
  useEffect(() => { latestStateRef.current = state; }, [state]);
  // True between "solo Start tapped" and the first round_started. While set,
  // `room_created` must NOT drop the user into a lobby — solo goes straight
  // to the wheel. See createSoloGame().
  const soloAutoStartRef = useRef(false);

  // Persist a snapshot of the live game state to localStorage on every
  // change — refresh recovery uses this as a fallback when the server's
  // getGameState returns active=false (cold start, transient socket
  // glitch, etc.). Capped at 5 min freshness.
  useEffect(() => {
    if (!state.roomCode || !state.isGameActive) {
      try { localStorage.removeItem('wr.gameState'); } catch {}
      return;
    }
    try {
      localStorage.setItem('wr.gameState', JSON.stringify({
        savedAt: Date.now(),
        active: true,
        roomCode: state.roomCode,
        isHost: state.isHost,
        status: 'playing',
        currentRound: state.currentRound,
        totalRounds: state.totalRounds,
        currentLetter: state.currentLetter,
        timeRemaining: state.timeRemaining,
        players: state.players,
        gameSettings: state.gameSettings,
      }));
    } catch {}
  }, [state.roomCode, state.isGameActive, state.currentRound, state.currentLetter, state.players]);

  // ✅ ENHANCED: Initialize WebSocket when user signs in
  useEffect(() => {
    const initializeConnection = async () => {
      console.log('🌐 WebSocketProvider: Checking connection status');

      if (!currentUser) {
        console.log('🌐 WebSocketProvider: No user found - waiting for authentication');
        dispatch({ type: 'SET_CONNECTION_STATUS', payload: 'disconnected' });
        initialized.current = false;
        return;
      }

      // Skip the re-auth dance ONLY if the socket is already authenticated
      // AS THIS SAME USER. If the uid changed (guest → Google sign-in,
      // sign-out + back in as someone else), force a fresh auth — the old
      // bug was that createRoom would fail with "Not authenticated"
      // because the socket was bound to a stale user id.
      const wsAuthUid = websocketService.getAuthenticatedUserId();
      if (
        initialized.current &&
        websocketService.isAuthenticated() &&
        websocketService.getConnectionStatus() === 'connected' &&
        wsAuthUid === currentUser.uid
      ) {
        console.log('🔌 WebSocket already connected and authenticated, skipping.');
        return;
      }
      // The uid changed — clear any stale auth so the new authenticate()
      // call binds the socket to the right user.
      if (wsAuthUid && wsAuthUid !== currentUser.uid) {
        console.log(`🔄 User changed (${wsAuthUid} → ${currentUser.uid}) — re-authenticating socket.`);
        websocketService.resetAuth();
      }

      initialized.current = true;
      dispatch({ type: 'SET_CONNECTION_STATUS', payload: 'connecting' });

      const playerName = userProfile?.displayName || currentUser.displayName || 'Player';
      // Avatar resolution mirrors the App.tsx player-init logic. Prefer the
      // saved avatar; if it's the default emoji '🎮' (which is what Firestore
      // gets seeded with on first signup), upgrade to the Google photoURL
      // so returning users see their real photo without having to re-pick.
      // Symptom this fixes: Arslan signed in pre-photoURL-support and stayed
      // on '🎮' forever even after refreshing.
      const stored = userProfile?.avatar;
      const photo = (currentUser as any).photoURL;
      const looksLikeUrl = stored && /^https?:\/\//i.test(stored);
      const looksLikeBrandId = stored && /^[a-z]{3,12}$/.test(stored);
      const playerAvatar =
        (looksLikeUrl || looksLikeBrandId) ? stored :
        photo || stored || 'fox';

      console.log(`🌐 WebSocketProvider: User found, connecting... ${playerName}`);

      // Teach the socket how to re-authenticate itself. On a reconnect the
      // server sees a brand-new socket with no user bound to it; without a
      // credential source the client comes back as a stranger and every
      // subsequent emit is silently ignored. Minting the token lazily here
      // also means we never replay a token that expired while offline.
      websocketService.setAuthProvider(async () => ({
        username: playerName,
        avatar: playerAvatar,
        firebaseUserId: currentUser.uid,
        firebaseToken: await currentUser.getIdToken(),
      }));

      try {
        await websocketService.connect();

        // Get Firebase token for backend authentication
        const firebaseToken = await currentUser.getIdToken();

        const authenticatedUser = await websocketService.authenticate(
          playerName,
          playerAvatar,
          currentUser.uid,
          firebaseToken
        );
        dispatch({ type: 'SET_PLAYER', payload: authenticatedUser });
        dispatch({ type: 'SET_CONNECTION_STATUS', payload: 'connected' });
        console.log('🌐 WebSocketProvider: Authenticated successfully');
        // Ask the server: "am I in a room? what state?" — without this
        // refreshing mid-game lands the user on a blank screen. The reply
        // (`gameStateRestore`) is now actually wired up; see below.
        websocketService.requestGameState();
      } catch (error) {
        console.error('WebSocket initialization failed:', error);
        dispatch({ type: 'SET_CONNECTION_STATUS', payload: 'error' });
        dispatch({ type: 'SET_ERROR', payload: 'Failed to connect to server.' });
        initialized.current = false; // Allow retry
      }
    };

    initializeConnection();

    // Auto-rejoin is intentionally DISABLED.
    //
    // It used to read localStorage on every WebSocketProvider mount and join
    // the saved room — but it raced with new-room flows (Solo Setup,
    // createRoom from Home), turning the user into a non-host in a stale
    // room while the new room was being created. The cure was worse than
    // the disease. Real reconnection within a session is handled by
    // socket.io's own reconnect logic; cross-session "drag me back in"
    // is a sprint 2+ feature that needs explicit consent.
    const checkForSavedRoom = async () => {
      // Best-effort: clear any stale flag on mount so it doesn't linger.
      const last = localStorage.getItem('lastGameActivity');
      if (last && Date.now() - parseInt(last) > 5 * 60 * 1000) {
        localStorage.removeItem('currentRoomCode');
        localStorage.removeItem('lastGameActivity');
      }
    };
    const rejoinTimer = setTimeout(checkForSavedRoom, 1000);

    // Define all event handlers
    const handleConnect = () => dispatch({ type: 'SET_CONNECTION_STATUS', payload: 'connected' });
    const handleDisconnect = () => dispatch({ type: 'SET_CONNECTION_STATUS', payload: 'disconnected' });
    const handleError = (data: { message: string }) => dispatch({ type: 'SET_ERROR', payload: data.message });
    const handleAuthenticated = (data: any) => {
      // Only seed currentPlayer if we don't already have one — App.tsx's
      // auth-hydration effect manages the canonical player record from
      // userProfile + photoURL, and overwriting here was wiping branded
      // avatars on every reconnect (server returns whatever's in DB which
      // is sometimes a stale '🎮').
      const existing = latestStateRef.current.currentPlayer;
      if (!existing || existing.id !== data.userId) {
        const player = {
          id: data.userId,
          name: data.username || existing?.name || 'Player',
          avatar: data.avatar || existing?.avatar || 'fox',
          isReady: false,
          isHost: false,
          score: 0,
        };
        dispatch({ type: 'SET_PLAYER', payload: player });
      }
    };
    const handleRoomCreated = (data: any) => {
      dispatch({ type: 'SET_ROOM_CODE', payload: data.roomCode });
      dispatch({ type: 'SET_HOST', payload: data.isHost });
      if (data.players) dispatch({ type: 'UPDATE_PLAYERS', payload: data.players });
      if (data.gameSettings) dispatch({ type: 'UPDATE_GAME_SETTINGS', payload: data.gameSettings });
      // SOLO: skip the lobby entirely. Sending the screen to 'lobby' here is
      // what stranded solo players — App.tsx routes on currentScreen, so they
      // landed in a lobby that nobody was ever going to start. Going straight
      // to 'game' shows the "preparing the round" wheel until round_started
      // arrives a moment later.
      dispatch({ type: 'SET_SCREEN', payload: soloAutoStartRef.current ? 'game' : 'lobby' });
      // Save room code for reconnection
      localStorage.setItem('currentRoomCode', data.roomCode);
      localStorage.setItem('lastGameActivity', Date.now().toString());
    };
    const handleRoomJoined = (data: any) => {
      dispatch({ type: 'JOIN_ROOM', payload: { roomCode: data.roomCode, players: data.players } });
      dispatch({ type: 'SET_HOST', payload: data.isHost });
      dispatch({ type: 'SET_SCREEN', payload: 'lobby' });
      // Save room code for reconnection
      localStorage.setItem('currentRoomCode', data.roomCode);
      localStorage.setItem('lastGameActivity', Date.now().toString());
    };
    const handlePlayerJoined = (data: any) => {
      console.log('🎉 Player joined:', data);
      // Server may send either the joined player solo, or a full list
      // (post-2026-04-28 addBot also broadcasts players_updated). We
      // handle both. Reading state.players from the closure was stale —
      // we now use a functional dispatch via the latestStateRef.
      if (data.players && Array.isArray(data.players)) {
        dispatch({ type: 'UPDATE_PLAYERS', payload: data.players });
        return;
      }
      if (data.player) {
        const current = latestStateRef.current.players;
        const existing = current.filter(p => p.id !== data.player.id);
        dispatch({ type: 'UPDATE_PLAYERS', payload: [...existing, data.player] });
      }
    };

    // Server's authoritative full-player-list refresh — the cleanest way
    // to keep the lobby in sync regardless of what triggered the change
    // (player joined, player left, bot added, ready toggled, etc.).
    const handlePlayersUpdated = (data: any) => {
      console.log('👥 Players updated:', data);
      if (data?.players && Array.isArray(data.players)) {
        dispatch({ type: 'UPDATE_PLAYERS', payload: data.players });
      }
    };
    const handlePlayerLeft = (data: any) => {
      console.log('👋 Player left:', data);

      const current = latestStateRef.current;
      if (data.players && Array.isArray(data.players)) {
        dispatch({ type: 'UPDATE_PLAYERS', payload: data.players });
      } else {
        dispatch({
          type: 'UPDATE_PLAYERS',
          payload: current.players.filter(p => p.id !== data.userId),
        });
      }

      if (data.gameState?.hostId) {
        dispatch({ type: 'SET_HOST', payload: current.currentPlayer?.id === data.gameState.hostId });
      }
    };
    const handlePlayerReadyChanged = (data: any) => {
      console.log('✅ Player ready changed:', data);
      const current = latestStateRef.current;

      if (data.gameState?.players) {
        dispatch({ type: 'UPDATE_PLAYERS', payload: data.gameState.players });
      } else {
        dispatch({
          type: 'UPDATE_PLAYERS',
          payload: current.players.map(p => p.id === data.userId ? { ...p, isReady: data.isReady } : p),
        });
      }

      if (data.gameState?.hostId) {
        dispatch({ type: 'SET_HOST', payload: current.currentPlayer?.id === data.gameState.hostId });
      }
    };
    const handleHostTransferred = (data: any) => {
      console.log('👑 Host transferred:', data);
      dispatch({ type: 'SET_HOST', payload: state.currentPlayer?.id === data.newHostId });
      // Update players list to reflect new host
      if (data.gameState?.players) {
        dispatch({ type: 'UPDATE_PLAYERS', payload: data.gameState.players });
      }
    };
    const handleRoundStarted = (data: any) => {
      soloAutoStartRef.current = false; // the solo hand-off is complete
      dispatch({ type: 'START_ROUND', payload: { roundNumber: data.roundNumber, letter: data.letter } });
      dispatch({ type: 'SET_SCREEN', payload: 'game' });
    };
    // The server owns the clock. When it tells us the time, believe it —
    // the client's local countdown is a display convenience, not truth.
    const handleTimerUpdate = (data: { timeRemaining: number }) => {
      if (typeof data?.timeRemaining === 'number') {
        dispatch({ type: 'UPDATE_TIMER', payload: Math.max(0, data.timeRemaining) });
      }
    };
    /**
     * The squeeze: someone hit Done, so everyone else is capped at
     * `squeezeSeconds` (host-configurable, default 10).
     *
     * A squeeze may only ever take time AWAY. If 4 seconds are left and the
     * table has the squeeze set to 30, the answer is still 4 — the server
     * clamps to `min(endsAt, now + n)` and we clamp here too, because a timer
     * that counts UPWARD on screen is a nonsense the player will never trust
     * again. Belt and braces: this guards against an older/looser server too.
     */
    const handleTimeSqueeze = (data: { byName: string; secondsLeft: number }) => {
      const secs = typeof data?.secondsLeft === 'number' ? data.secondsLeft : 10;
      const current = latestStateRef.current.timeRemaining;
      const next = Math.max(0, Math.min(secs, current > 0 ? current : secs));
      dispatch({ type: 'UPDATE_TIMER', payload: next });
    };
    const handleAnswerSubmitted = (data: any) => {
      // Use latestStateRef to avoid the stale-closure bug. Reading
      // state.players from the effect closure gave us the empty array
      // from mount-time, so the first answer-submitted event blew the
      // entire player list away — that's why "players stopped showing
      // up after first round".
      const current = latestStateRef.current.players;
      dispatch({
        type: 'UPDATE_PLAYERS',
        // Both spellings, because both exist: the server serializes
        // `hasSubmitted` and GameScreen's roster-sync reads it — setting only
        // the local `isSubmitted` left the two views of "who has gone" able
        // to disagree about the player standing right in front of you.
        payload: current.map(p => p.id === data.playerId ? { ...p, isSubmitted: true, hasSubmitted: true } : p),
      });
    };
    const handleRoundEnded = (data: any) => dispatch({ type: 'ROUND_COMPLETE', payload: { scores: data.scores } });
    const handleGameEnded = (data: any) => {
      // Tolerate legacy { results: ... } shape AND the new pass-through
      // shape (raw gameEnded payload). Either way, gameResults should
      // have `.players` (ranked list with totalScore).
      const gameResults = data?.results?.players ? data.results : data;
      dispatch({ type: 'GAME_COMPLETE', payload: gameResults });
      dispatch({ type: 'SET_GAME_RESULTS', payload: gameResults });
      // Clear localStorage so refreshing post-game doesn't drag the user
      // back into the completed room.
      try { localStorage.removeItem('wr.gameState'); } catch {}
      try { localStorage.removeItem('currentRoomCode'); } catch {}
      try { localStorage.removeItem('lastGameActivity'); } catch {}
      dispatch({ type: 'SET_SCREEN', payload: 'results' });
      // Clear saved room code when game ends
      localStorage.removeItem('currentRoomCode');
      localStorage.removeItem('lastGameActivity');
    };
    const handleGameState = (data: any) => {
        // Only update players if we have data
        if (data.players && Array.isArray(data.players)) {
          dispatch({ type: 'UPDATE_PLAYERS', payload: data.players });
        }
        if (data.gameSettings) dispatch({ type: 'UPDATE_GAME_SETTINGS', payload: data.gameSettings });
    };
    /**
     * The host changed the rules — or opened the room to strangers.
     *
     * The server has broadcast this to the whole lobby all along and nothing
     * listened, so every non-host sat looking at stale settings while the host
     * saw their own optimistic local dispatch. That was cosmetic for the squeeze
     * timer. It is not cosmetic for visibility: a player must see the room being
     * opened to the public at the moment it happens, not discover it when a
     * stranger walks in.
     */
    const handleGameSettingsUpdated = (data: any) => {
      if (data?.gameSettings) dispatch({ type: 'UPDATE_GAME_SETTINGS', payload: data.gameSettings });
    };
    const handleRoomNotFound = (data: any) => dispatch({ type: 'SET_ERROR', payload: `Room ${data.roomCode} not found.` });
    const handleRoomFull = (data: any) => dispatch({ type: 'SET_ERROR', payload: `Room ${data.roomCode} is full.` });
    
    const handleKicked = (data: any) => {
      console.log('👟 You have been kicked:', data);
      dispatch({ type: 'LEAVE_ROOM' });
      dispatch({ type: 'SET_ERROR', payload: data.message || 'You have been kicked from the room' });
      dispatch({ type: 'SET_SCREEN', payload: 'home' });
      // Clear saved room code
      localStorage.removeItem('currentRoomCode');
      localStorage.removeItem('lastGameActivity');
      // Force a navigation away from the room URL — without this the
      // kicked client stays parked on /room/:code and just shows an empty
      // lobby. Using a DOM event lets us trigger react-router's navigate
      // from outside React (we don't have access to useNavigate here).
      try {
        window.dispatchEvent(new CustomEvent('wr:navigate', { detail: { to: '/home', replace: true } }));
      } catch {}
    };

    const handlePlayerKicked = (data: any) => {
      console.log('👟 Player kicked:', data);
      // Backend now sends serialized players via players_updated; this
      // event is for display only (toast / "X was kicked"). Belt-and-
      // suspenders: still apply the snapshot in case the broadcast order
      // arrives playerKicked first.
      if (data.players) {
        dispatch({ type: 'UPDATE_PLAYERS', payload: data.players });
      }
    };
    
    const handleRematchStarted = (data: any) => {
      console.log('🔄 Rematch started:', data);
      // Reset game state
      dispatch({ type: 'LEAVE_ROOM' }); // Clear current state
      dispatch({ type: 'JOIN_ROOM', payload: { roomCode: data.roomCode, players: data.players } });
      dispatch({ type: 'SET_SCREEN', payload: 'lobby' });
    };

    /**
     * The single most important listener in the app, and until now it was
     * never registered: the backend HAS been emitting `gameStateRestore`
     * (server.js `getGameState`) the whole time, but websocketService never
     * forwarded it — so refresh / reconnect state restore was completely,
     * silently dead. It is wired end-to-end now.
     */
    const handleGameStateRestore = (data: any) => {
      console.log('🔁 gameStateRestore received:', data);
      dispatch({ type: 'RESTORE_GAME_STATE', payload: data });
    };

    // Attach all event handlers
    websocketService.on('connect', handleConnect);
    websocketService.on('disconnect', handleDisconnect);
    websocketService.on('error', handleError);
    websocketService.on('authenticated', handleAuthenticated);
    websocketService.on('room_created', handleRoomCreated);
    websocketService.on('room_joined', handleRoomJoined);
    websocketService.on('playerJoined', handlePlayerJoined);
    websocketService.on('playerLeft', handlePlayerLeft);
    websocketService.on('playerReadyChanged', handlePlayerReadyChanged);
    websocketService.on('hostTransferred', handleHostTransferred);
    websocketService.on('round_started', handleRoundStarted);
    websocketService.on('answer_submitted', handleAnswerSubmitted);
    websocketService.on('round_ended', handleRoundEnded);
    websocketService.on('game_ended', handleGameEnded);
    websocketService.on('gameState', handleGameState);
    websocketService.on('gameStateRestore', handleGameStateRestore);
    websocketService.on('timer_update', handleTimerUpdate);
    websocketService.on('time_squeeze', handleTimeSqueeze);
    websocketService.on('gameSettingsUpdated', handleGameSettingsUpdated);
    websocketService.on('room_not_found', handleRoomNotFound);
    websocketService.on('room_full', handleRoomFull);
    websocketService.on('kicked', handleKicked);
    websocketService.on('playerKicked', handlePlayerKicked);
    websocketService.on('rematchStarted', handleRematchStarted);
    websocketService.on('players_updated', handlePlayersUpdated);

    // ✅ CRITICAL FIX: Cleanup function to remove all listeners on component unmount
    return () => {
      console.log('🔌 Cleaning up WebSocket event listeners');
      websocketService.off('connect', handleConnect);
      websocketService.off('disconnect', handleDisconnect);
      websocketService.off('error', handleError);
      websocketService.off('authenticated', handleAuthenticated);
      websocketService.off('room_created', handleRoomCreated);
      websocketService.off('room_joined', handleRoomJoined);
      websocketService.off('playerJoined', handlePlayerJoined);
      websocketService.off('playerLeft', handlePlayerLeft);
      websocketService.off('playerReadyChanged', handlePlayerReadyChanged);
      websocketService.off('hostTransferred', handleHostTransferred);
      websocketService.off('round_started', handleRoundStarted);
      websocketService.off('answer_submitted', handleAnswerSubmitted);
      websocketService.off('round_ended', handleRoundEnded);
      websocketService.off('game_ended', handleGameEnded);
      websocketService.off('gameState', handleGameState);
      websocketService.off('gameStateRestore', handleGameStateRestore);
      websocketService.off('timer_update', handleTimerUpdate);
      websocketService.off('time_squeeze', handleTimeSqueeze);
      websocketService.off('gameSettingsUpdated', handleGameSettingsUpdated);
      websocketService.off('room_not_found', handleRoomNotFound);
      websocketService.off('room_full', handleRoomFull);
      websocketService.off('kicked', handleKicked);
      websocketService.off('playerKicked', handlePlayerKicked);
      websocketService.off('rematchStarted', handleRematchStarted);
      websocketService.off('players_updated', handlePlayersUpdated);
      clearTimeout(rejoinTimer);
    };
  }, [currentUser, userProfile, dispatch]); // Add currentUser and userProfile as dependencies

  // Add handler to save activity timestamp when game is active
  useEffect(() => {
    if (state.roomCode && (state.currentScreen === 'lobby' || state.currentScreen === 'game')) {
      const activityInterval = setInterval(() => {
        localStorage.setItem('lastGameActivity', Date.now().toString());
      }, 30000); // Update every 30 seconds
      
      return () => clearInterval(activityInterval);
    }
  }, [state.roomCode, state.currentScreen]);

  // WebSocket API methods
  const connect = useCallback(async () => {
    if (state.connectionStatus === 'connected' || state.connectionStatus === 'connecting') {
      console.log('🌐 Already connected/connecting, skipping...');
      return;
    }
    
    console.log('🌐 Manual connect requested');
    await websocketService.connect();
  }, [state.connectionStatus]);

  const disconnect = useCallback(() => {
    console.log('🌐 Disconnecting...');
    websocketService.disconnect();
  }, []);

  const authenticate = useCallback(async (name: string, avatar: string) => {
    console.log('🌐 Authenticating:', name);
    return await websocketService.authenticate(name, avatar);
  }, []);

  const createRoom = useCallback(async (options?: any) => {
    console.log('🌐 Creating room with options:', options);
    if (!state.currentPlayer) {
      throw new Error('No current player for room creation');
    }
    const code = await websocketService.createRoom(state.currentPlayer, options);

    /*
     * guest_became_host — per the memo, the only viral metric that matters.
     *
     * Somebody arrived here because a friend shared a game with them, and they
     * have just started a table of their own. That is the loop closing, and it
     * is the ONE number worth steering by: shares sent, link clicks and installs
     * can all go up while the product quietly dies.
     *
     * Fired from `createRoom` because it is the single choke point every game
     * creation passes through (Play Now, solo, private). It is a no-op for
     * anyone who did not arrive via a share, it fires at most once ever, and it
     * is awaited-but-swallowed — analytics must never be able to fail a game.
     */
    void markBecameHost(typeof code === 'string' ? code : undefined);

    return code;
  }, [state.currentPlayer]);

  const joinRoom = useCallback(async (roomCode: string) => {
    console.log('🌐 Joining room:', roomCode);
    if (!state.currentPlayer) {
      throw new Error('No current player for room joining');
    }
    await websocketService.joinRoom(roomCode, state.currentPlayer);

    /*
     * You just sat down at somebody else's table. Stamp it — first write wins,
     * so this never overwrites the share link that actually recruited you.
     *
     * Without this, `guest_became_host` could only ever fire for people who
     * arrived through a URL we generated. The most common invitation in this
     * product is somebody reading a room code out loud across a kitchen, and
     * that person becoming a host next week is exactly the conversion Arslan
     * asked to see.
     */
    rememberReferral('joined-room', roomCode);
  }, [state.currentPlayer]);

  const leaveRoom = useCallback(async () => {
    console.log('🌐 Leaving room');
    if (!state.currentPlayer) {
      throw new Error('No current player for leaving room');
    }
    await websocketService.leaveRoom(state.currentPlayer.id);
    // Clear saved room data when leaving
    localStorage.removeItem('currentRoomCode');
    localStorage.removeItem('lastGameActivity');
  }, [state.currentPlayer]);

  const setPlayerReady = useCallback(async (isReady: boolean) => {
    console.log('🌐 Setting player ready:', isReady);
    if (!state.currentPlayer) {
      throw new Error('No current player for ready status');
    }
    await websocketService.setPlayerReady(state.currentPlayer.id, isReady);
  }, [state.currentPlayer]);

  const startGame = useCallback(async () => {
    console.log('🌐 Starting game');
    await websocketService.startGame();
  }, []);

  /**
   * Solo, end to end: create → (ensure exactly one Atom) → start.
   *
   * What was broken: SoloSetupScreen created a room, added a bot, and then
   * navigated to `/room/:code` — a LOBBY. Nothing ever started the game. A
   * first-time player tapped "Start 5-round game", landed in a waiting room,
   * and reasonably concluded the app was broken.
   *
   * The second bug lived here too: the backend ALREADY spawns Atom inside
   * `createRoom` when `isSinglePlayer` is set (it reads `settings.difficulty`
   * — see gameLogic.initializeSinglePlayerBots). The client then added a
   * SECOND Atom. Two identical bots then duplicated each other's answers and
   * handed each other 5s, wrecking the scoring. So: pass difficulty at
   * create time, and only add a bot if the server somehow didn't.
   */
  const createSoloGame = useCallback(async (options: { rounds: number; difficulty: string }) => {
    if (!state.currentPlayer) throw new Error('No current player for room creation');
    soloAutoStartRef.current = true;
    // Capture the authoritative roster straight off the room_created payload.
    // Reading it from React state here would race the reducer flush.
    let roster: Player[] = [];
    const captureRoster = (data: any) => { roster = data?.players || []; };
    websocketService.on('room_created', captureRoster);
    try {
      const roomCode = await websocketService.createRoom(state.currentPlayer, {
        rounds: options.rounds,
        isSinglePlayer: true,
        gameMode: 'solo',
        difficulty: options.difficulty,
      });

      const botCount = roster.filter(p => p.isBot).length;
      if (botCount === 0) {
        console.log('🤖 Server did not seed a bot — adding Atom.');
        await websocketService.addBot('atom', { difficulty: options.difficulty as any });
      } else if (botCount > 1) {
        console.warn(`🤖 Solo room has ${botCount} bots — expected 1.`);
      }

      await websocketService.startGame();
      return roomCode;
    } catch (e) {
      // Failed to start → fall back to the lobby so the user isn't stranded
      // on a spinner. At least there they have a Start button.
      soloAutoStartRef.current = false;
      dispatch({ type: 'SET_SCREEN', payload: 'lobby' });
      throw e;
    } finally {
      websocketService.off('room_created', captureRoster);
    }
  }, [state.currentPlayer, dispatch]);

  /**
   * START A RUN.
   *
   * Note what is NOT passed: a difficulty, and a round count. Both are the
   * server's, and that is the entire point of the Ladder — self-selected
   * difficulty is self-selected boredom. You do not get to choose which Atom you
   * face; you climb until one of them beats you.
   *
   * Everything else is the solo path, which already worked: create → the server
   * seeds exactly one Atom → start → straight to the wheel, no lobby, because
   * there is nobody to wait for.
   */
  const createLadderRun = useCallback(async () => {
    if (!state.currentPlayer) throw new Error('No current player for room creation');
    soloAutoStartRef.current = true;
    try {
      const roomCode = await websocketService.createRoom(state.currentPlayer, {
        isLadder: true,
        isSinglePlayer: true,
        gameMode: 'ladder',
      });
      await websocketService.startGame();
      return roomCode;
    } catch (e) {
      soloAutoStartRef.current = false;
      dispatch({ type: 'SET_SCREEN', payload: 'lobby' });
      throw e;
    }
  }, [state.currentPlayer, dispatch]);

  const reinitializeConnection = useCallback(async () => {
    console.log('🌐 Reinitializing connection');
    await websocketService.connect();
  }, []);

  const sendChatMessage = useCallback(async (message: string) => {
    console.log('🌐 Sending chat message:', message);
    if (!state.currentPlayer) {
      throw new Error('No current player for sending message');
    }
    await websocketService.sendMessage(state.currentPlayer.id, message);
  }, [state.currentPlayer]);

  const submitAnswers = useCallback(async (answers: Record<string, string>) => {
    console.log('🌐 Submitting answers:', answers);
    await websocketService.submitAnswers(answers);
  }, []);

  const kickPlayer = useCallback(async (playerId: string) => {
    console.log('👟 Kicking player:', playerId);
    try {
      await websocketService.kickPlayer(playerId);
    } catch (error: any) {
      console.error('Failed to kick player:', error);
      dispatch({ type: 'SET_ERROR', payload: error?.message || 'Failed to kick player' });
      throw error;
    }
  }, [dispatch]);

  const transferHost = useCallback(async (playerId: string) => {
    try {
      await websocketService.transferHost(playerId);
    } catch (error: any) {
      console.error('Failed to transfer host:', error);
      dispatch({ type: 'SET_ERROR', payload: error?.message || 'Failed to make that player host' });
      throw error;
    }
  }, [dispatch]);

  const addBot = useCallback(async (difficulty?: string) => {
    try {
      await websocketService.addBot('atom', { difficulty: (difficulty as any) || 'medium' });
    } catch (error: any) {
      console.error('Failed to add bot:', error);
      dispatch({ type: 'SET_ERROR', payload: error?.message || 'Could not add Atom' });
      throw error;
    }
  }, [dispatch]);

  const getCurrentRoom = useCallback(() => {
    return websocketService.getCurrentRoom();
  }, []);
  
  const requestRematch = useCallback(async () => {
    console.log('🔄 Requesting rematch...');
    try {
      await websocketService.requestRematch();
    } catch (error) {
      console.error('Failed to request rematch:', error);
      dispatch({ type: 'SET_ERROR', payload: 'Failed to start rematch' });
    }
  }, [dispatch]);

  const contextValue: WebSocketContextType = {
    isConnected: state.connectionStatus === 'connected',
    connectionStatus: state.connectionStatus,
    currentRoom: websocketService.getCurrentRoom(),
    connect,
    disconnect,
    authenticate,
    createRoom,
    joinRoom,
    leaveRoom,
    setPlayerReady,
    startGame,
    createSoloGame,
    createLadderRun,
    reinitializeConnection,
    sendChatMessage,
    submitAnswers,
    kickPlayer,
    transferHost,
    addBot,
    getCurrentRoom,
    requestRematch
  };

  return (
    <WebSocketContext.Provider value={contextValue}>
      {children}
    </WebSocketContext.Provider>
  );
} 